import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  Skeleton,
} from "@mui/material";
import { createNodeRenderer } from "./TaskAgentSections";
import { ImageURLDialog } from "components/common/AttachmentDialog";

// Normalize href to ensure it has a valid protocol
export const cleanHref = (href: string): string => {
  if (!href) return href;
  if (href.startsWith("http") || href.startsWith("/")) {
    return href;
  }
  return `https://${href}`;
};

type CellData = {
  type: string;
  element?: Element;
  content: string;
  href?: string | null;
  src?: string | null;
  title?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
};

const findImageSrc = (element: Element): string | null => {
  // Check if the element itself is an img
  if (element.tagName.toLowerCase() === "img") {
    return (element as HTMLImageElement).getAttribute("src");
  }

  // Check all children recursively
  for (const child of Array.from(element.children)) {
    const src = findImageSrc(child);
    if (src) {
      return src;
    }
  }

  // Return null if no img found
  return null;
};

type TableData = {
  title?: string | null;
  rows: (CellData | null)[][];
};

const renderCell = (cell: CellData, openImage?: (url: string) => void) => {
  const renderNode = createNodeRenderer();

  // Helper to render cell content with nested tags
  const renderContent = () => {
    if (!cell.element) {
      // Fallback to plain content if no element available
      return cell.content;
    }
    return Array.from(cell.element.childNodes as NodeListOf<ChildNode>).map(
      (child, idx) => (
        <React.Fragment key={idx}>{renderNode(child)}</React.Fragment>
      ),
    );
  };

  switch (cell.type) {
    case "th":
      return (
        <Typography variant="bodyHeavy" sx={{ fontWeight: 600 }}>
          {renderContent()}
        </Typography>
      );
    case "td":
      const isImageUrl =
        cell.content &&
        (cell.content.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i) ||
          (cell.content.startsWith("http") &&
            (cell.content.includes("image") ||
              cell.content.includes("photo") ||
              cell.content.includes("picture") ||
              cell.content.includes("thumbnail"))));

      if (cell.src || isImageUrl) {
        return (
          <Box
            component="img"
            src={cell.src || cell.content}
            alt="Product image"
            sx={{
              maxWidth: "100px",
              height: "auto",
              objectFit: "contain",
              cursor: "pointer",
            }}
            onClick={() => openImage?.(cell.src || "")}
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.parentElement!.innerHTML = "📸";
            }}
          />
        );
      }

      return cell.href ? (
        <Link
          href={cleanHref(cell.href)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {renderContent()}
        </Link>
      ) : (
        <Typography variant="body">{renderContent()}</Typography>
      );
    case "img":
      return (
        <Box
          component="img"
          src={cell.src || ""}
          alt=""
          onClick={() => openImage?.(cell.src || "")}
          sx={{ maxWidth: "100px", height: "auto", cursor: "pointer" }}
        />
      );
    default:
      return <Typography variant="body">{renderContent()}</Typography>;
  }
};

const TableView = ({
  isActive,
  tableData,
}: {
  isActive: boolean;
  tableData: TableData;
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  if (!tableData.rows.length) {
    if (!isActive) return null;
    return (
      <Box sx={{ margin: "16px 0", opacity: 0.7 }}>
        {tableData.title && (
          <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
            {tableData.title}
          </Typography>
        )}
        <Typography variant="body" sx={{ fontStyle: "italic" }}>
          Loading table data...
        </Typography>
      </Box>
    );
  }
  // Determine if first row is header
  const hasHeaderRow = tableData.rows[0]?.some((cell) => cell?.type === "th");
  const numColumns = tableData.rows[0]?.length || 0;

  return (
    <Box sx={{ margin: "16px 0" }}>
      {tableData.title && (
        <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
          {tableData.title}
        </Typography>
      )}
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Table size="small" sx={{ width: "100%" }}>
          {hasHeaderRow && (
            <TableHead>
              <TableRow>
                {tableData.rows[0].map((cell, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      paddingBottom: "8px",
                    }}
                  >
                    {cell && renderCell(cell)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {tableData.rows.slice(hasHeaderRow ? 1 : 0).map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    sx={{
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    }}
                  >
                    {cell && renderCell(cell, setImageUrl)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {isActive && (
              <TableRow>
                {/* Loading skeleton row */}
                {Array.from({ length: numColumns }).map((_, index) => (
                  <TableCell
                    key={`skeleton-${index}`}
                    sx={{
                      verticalAlign: "top",
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    }}
                  >
                    <Skeleton />
                    {index % 2 === 1 && <Skeleton width="80%" />}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <ImageURLDialog
        attachmentUrl={imageUrl || ""}
        onClose={() => setImageUrl(null)}
      />
    </Box>
  );
};

// ChatMessageTable component for rendering XML tables
export const ChatMessageTable = ({ xmlContent }: { xmlContent: string }) => {
  const parseTableXML = (xml: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, "text/html");
    const table = doc.querySelector("table");

    if (!table) return null;

    const title = table.getAttribute("data-title");
    const rows = Array.from(table.querySelectorAll("tr"));

    return {
      title,
      rows: rows.map((row) => {
        const cells = Array.from(row.children);
        return cells.map((cell) => ({
          type: cell.tagName.toLowerCase(),
          element: cell,
          content: cell.textContent || "",
          href: cell.getAttribute("href") || cell.getAttribute("data-url"),
          src: cell.getAttribute("src") || findImageSrc(cell),
          title: cell.getAttribute("data-title"),
          address: cell.getAttribute("data-address"),
          city: cell.getAttribute("data-city"),
          state: cell.getAttribute("data-state"),
        }));
      }),
    };
  };

  const tableData = parseTableXML(xmlContent);

  if (!tableData) return null;

  return <TableView isActive={false} tableData={tableData} />;
};

// PartialChatMessageTable component for progressive table loading
export const PartialChatMessageTable = ({
  xmlContent,
}: {
  xmlContent: string;
}) => {
  const parsePartialTableXML = (xml: string) => {
    // Normalize whitespace within tags to handle streaming tokens
    // This handles cases where tags are split like: <tr\n> or <table\ndata-title\n="\n...">
    const normalizedXml = xml.replace(/<[^>]*>/gs, (match) => {
      // Replace any whitespace (newlines, spaces, etc.) within tags with single spaces
      return match.replace(/\s+/g, " ");
    });

    // Parse incomplete table XML - look for data-title attribute with flexible whitespace
    // Only match if we have a complete opening table tag (ends with >)
    const titleMatch = normalizedXml.match(
      /<table[^>]*data-title\s*=\s*"([^"]*)"[^>]*>/,
    );
    const title = titleMatch ? titleMatch[1] : null;

    // Find complete rows only (rows with closing </tr> tag)
    // Use \s* to handle whitespace between tags that may have been normalized
    const completeRowMatches = normalizedXml.match(/<tr\s*>.*?<\/tr>/gs) || [];

    const parser = new DOMParser();
    const rows = completeRowMatches
      .map((rowXml) => {
        try {
          const doc = parser.parseFromString(rowXml, "text/xml");

          // Check for parse errors
          const parseError = doc.querySelector("parsererror");
          if (parseError) {
            return [];
          }

          const row = doc.querySelector("tr");
          if (!row) return [];

          return Array.from(row.children).map((cell) => ({
            type: cell.tagName.toLowerCase(),
            element: cell,
            content: cell.textContent || "",
            href: cell.getAttribute("href") || cell.getAttribute("data-url"),
            src: cell.getAttribute("src") || findImageSrc(cell),
            title: cell.getAttribute("data-title"),
            address: cell.getAttribute("data-address"),
            city: cell.getAttribute("data-city"),
            state: cell.getAttribute("data-state"),
          }));
        } catch (e) {
          // If parsing fails, skip this row
          return [];
        }
      })
      .filter((row) => row.length > 0);

    return { title, rows };
  };

  const tableData = parsePartialTableXML(xmlContent);
  return <TableView isActive={true} tableData={tableData} />;
};
